// Guards the whole class declaration, not just customElements.define - this
// file gets loaded via a separate <script src> tag from every section that
// uses it (testimonials, product carousels, etc.), and more than one of
// those can appear on the same page. A top-level `class` redeclared by a
// second <script> in the same document throws a SyntaxError (classic
// scripts share one global lexical scope for let/const/class), which was
// silently breaking the second-and-later instances' setup on any page
// where multiple carousels coexist.
if (!customElements.get('rizon-scroll-carousel')) {
  class RizonScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.counter = this.querySelector('[data-carousel-counter]');
      // Each dot button wraps a rizon-carousel-progress span - that inner span
      // carries the classes the CSS actually targets, not the button itself.
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]')).map((button) => ({
        button,
        progress: button.querySelector('.rizon-carousel-progress'),
      }));
      if (!this.track) return;

      this.track.style.scrollBehavior = 'smooth';
      this.items = Array.from(this.track.querySelectorAll('[data-carousel-item]'));

      this.prevButton?.addEventListener('click', () => this.goToIndex((this.activeIndex || 0) - 1));
      this.nextButton?.addEventListener('click', () => this.goToIndex((this.activeIndex || 0) + 1));
      this.dots.forEach(({ button }, index) => button.addEventListener('click', () => this.goToIndex(index)));
      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

      this.autoplayDelay = parseInt(this.getAttribute('data-autoplay'), 10) || 0;
      if (this.autoplayDelay > 0) {
        this.style.setProperty('--rizon-progress-duration', `${this.autoplayDelay}ms`);
      }

      this.setActiveIndex(0);
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    // Button/dot/autoplay-driven navigation always advances exactly one
    // index and sets it authoritatively immediately, rather than waiting to
    // re-derive the active index from wherever the scroll animation happens
    // to land. That derivation (syncActiveFromScrollPosition, used only for
    // manual drag/swipe) can't always tell trailing items apart once the
    // track runs out of room to scroll further - the true last item's left
    // edge can never reach the track's left edge when the last couple of
    // items collectively fit within the viewport, so distance-based
    // detection permanently favors an earlier item, both getting the
    // carousel stuck one item early AND hiding the next arrow at that same
    // wrong boundary (arrow visibility below is index-based, never
    // scroll-position-based, for the same reason).
    //
    // suppressNextSync exists because mandatory scroll-snap can silently
    // auto-correct the scroll position we just set to a neighboring item's
    // snap point (e.g. index 5 and 6 can sit close enough together once
    // scroll room runs out that snapping "corrects" one into the other).
    // That still fires a real 'scroll' event, which would otherwise let the
    // passive listener re-derive - and override - the index we just set
    // authoritatively, which is exactly what caused the 6th slide to be
    // shown for an instant and then immediately replaced.
    goToIndex(index) {
      const clamped = Math.max(0, Math.min(this.items.length - 1, index));
      const item = this.items[clamped];
      if (!item) return;
      const itemRect = item.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      this.suppressNextSync = true;
      this.track.scrollLeft = this.track.scrollLeft + (itemRect.left - trackRect.left);
      this.setActiveIndex(clamped);
    }

    onScroll() {
      window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        if (this.suppressNextSync) {
          this.suppressNextSync = false;
          return;
        }
        this.syncActiveFromScrollPosition();
      }, 100);
    }

    // Only used to detect where a manual drag/swipe/trackpad scroll
    // settled - button/dot/autoplay navigation goes through setActiveIndex
    // directly via goToIndex instead.
    syncActiveFromScrollPosition() {
      if (!this.items.length) return;
      const trackRect = this.track.getBoundingClientRect();
      let closestIndex = 0;
      const atMaxScroll = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1;
      if (atMaxScroll) {
        closestIndex = this.items.length - 1;
      } else {
        let closestDistance = Infinity;
        this.items.forEach((item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - trackRect.left);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
      }
      this.setActiveIndex(closestIndex);
    }

    updateArrowVisibility() {
      const index = this.activeIndex || 0;
      if (this.prevButton) {
        this.prevButton.hidden = index <= 0;
      }
      if (this.nextButton) {
        this.nextButton.hidden = index >= this.items.length - 1;
      }
    }

    setActiveIndex(index) {
      const active = this.items[index];
      if (!active) return;
      this.items.forEach((item) => item.classList.toggle('is-active', item === active));
      if (this.counter) {
        this.counter.textContent = `${index + 1} / ${this.items.length}`;
      }
      this.activeIndex = index;
      this.updateArrowVisibility();
      this.restartDots(index);
      this.restartLoader(active);
      this.scheduleAutoplay();
    }

    restartLoader(activeItem) {
      this.items.forEach((item) => {
        const fill = item.querySelector('.rizon-loader-testimonial__fill');
        if (fill) fill.classList.remove('is-filling');
      });

      const activeFill = activeItem.querySelector('.rizon-loader-testimonial__fill');
      if (!activeFill) return;
      // Force reflow so re-adding the class restarts the fill transition.
      void activeFill.getBoundingClientRect();
      activeFill.classList.add('is-filling');
    }

    // Same fill-restart trick as restartLoader: when --rizon-progress-duration
    // is left at its default (autoplaying carousels like testimonials), the
    // dot widens then fills over that duration. When a wrapper sets it to 0s
    // (non-autoplaying carousels like the product carousels), it just snaps
    // straight to the filled state - no animation, matching the plain
    // Default/Filled states of the Button - Slide component.
    restartDots(activeIndex) {
      this.dots.forEach(({ progress }, index) => {
        if (!progress) return;
        progress.classList.remove('is-filling');
        progress.classList.toggle('rizon-carousel-progress--active', index === activeIndex);
      });

      const activeProgress = this.dots[activeIndex]?.progress;
      if (!activeProgress) return;
      void activeProgress.getBoundingClientRect();
      activeProgress.classList.add('is-filling');
    }

    // Reschedule from the moment each fill actually starts (not a fixed
    // interval) so the advance always lands exactly when the fill completes.
    scheduleAutoplay() {
      if (!this.autoplayDelay) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setTimeout(() => {
        const nextIndex = (this.activeIndex + 1) % this.items.length;
        this.goToIndex(nextIndex);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
    }
  }

  customElements.define('rizon-scroll-carousel', RizonScrollCarousel);
}
